import axios from 'axios';

/**
 * The reasons the delete endpoints refuse a patient delete with a 400 whose
 * body carries `{ detail: { error_code } }` (COLAB-447; the payload is
 * COLAB-445's). The shape is ml-scribe's `client_error_detail` envelope,
 * pinned by the integration tests at
 * `domains/patient_profile/tests/api/test_int_delete_foreign_session_refused.py`
 * (`body["detail"]["type"] == "PatientProfileAccessDeniedError"`,
 * `body["detail"]["error_code"] == "foreign_session_present"`).
 *
 * Underscore spelling on purpose: these are the backend's strings. The UI
 * union (`PatientDeleteBlockedReason`) keeps its own kebab-case spelling and
 * `mapBeRefusalReason` translates between the two at the host, so a backend
 * rename touches one row rather than three dialog sites.
 */
export type PatientDeleteBeRefusalReason = 'not_manager' | 'foreign_session_present';

const KNOWN_REFUSAL_REASONS: ReadonlySet<string> = new Set<PatientDeleteBeRefusalReason>([
  'not_manager',
  'foreign_session_present',
]);

const isKnownRefusalReason = (value: unknown): value is PatientDeleteBeRefusalReason =>
  typeof value === 'string' && KNOWN_REFUSAL_REASONS.has(value);

/**
 * The refusal reason on a failed delete, or `null` when the failure carries
 * none the UI can explain: any status other than 400, no response at all, a
 * body that is not an object, a missing/non-object `detail`, or an
 * `error_code` this build does not know. Every `null` falls through to the
 * caller's generic failure path — an unknown reason is a toast, never a
 * silent block.
 *
 * Scoped to 400 rather than `>= 400`: a 403 from COLAB-343's `managed_by`
 * check and a 500 both lack a reason body, and widening the status would
 * accept any future 4xx that happens to carry an `error_code` for an
 * unrelated meaning.
 */
export function readPatientDeleteRefusalReason(
  error: unknown
): PatientDeleteBeRefusalReason | null {
  if (!axios.isAxiosError(error)) return null;
  if (error.response?.status !== 400) return null;
  const data: unknown = error.response.data;
  if (typeof data !== 'object' || data === null) return null;
  const detail: unknown = (data as { detail?: unknown }).detail;
  if (typeof detail !== 'object' || detail === null) return null;
  const errorCode: unknown = (detail as { error_code?: unknown }).error_code;
  return isKnownRefusalReason(errorCode) ? errorCode : null;
}
